import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { CURRENCY } from 'App';
import { useMultiBonus } from 'hooks/useMultiBonus';
import { SlipEntry } from 'types/SlipEntry';
import {
  multiBonusAmount,
  multiBonusMatches,
  multiBonusPercent,
  nextMultiBonusTier,
} from 'utils/multiBonus';
import styles from './MultiBonus.module.css';

const money = (n: number) => `${n.toFixed(2)} ${CURRENCY}`;

interface SlipRowProps {
  entries: SlipEntry[];
  /** What the slip pays without the bonus (already within the maximum). */
  win: number;
  maxWin: number;
  className?: string;
}

/**
 * The bonus on the number of matches under the slip's figures: what this slip
 * earns on top and what the next match would bring, or how many more matches
 * the first tier needs.
 */
export const MultiBonusSlipRow = ({
  entries,
  win,
  maxWin,
  className,
}: SlipRowProps) => {
  const { t } = useTranslation();
  const rules = useMultiBonus();
  if (!rules.enabled || !rules.tiers?.length || entries.length === 0) {
    return null;
  }
  const minOdds = rules.min_odds ?? 1;
  const n = multiBonusMatches(entries, minOdds);
  const percent = multiBonusPercent(rules.tiers, n);
  const next = nextMultiBonusTier(rules.tiers, n);
  const odds = minOdds.toFixed(2);

  if (percent <= 0) {
    if (!next) return null;
    return (
      <div className={classNames(styles.slipHint, className)}>
        {t('multi_bonus_hint_label', {
          count: next.matches - n,
          percent: next.percent,
          odds,
        })}
      </div>
    );
  }

  const bonus = multiBonusAmount(win, percent, rules.max_bonus ?? 0, maxWin);
  return (
    <div className={classNames(styles.slipBox, className)}>
      <div className={styles.slipHead}>
        <span className={styles.badge}>
          {t('multi_bonus_badge', { percent })}
        </span>
        <span className={styles.slipTitle}>{t('multi_bonus_label')}</span>
        {bonus > 0 && (
          <strong className={styles.slipAmount}>+{money(bonus)}</strong>
        )}
      </div>
      {bonus > 0 ? (
        <div className={styles.slipTotal}>
          <span>{t('multi_bonus_total_label')}</span>
          <strong>{money(win + bonus)}</strong>
        </div>
      ) : (
        win >= maxWin && (
          <small className={styles.slipNext}>
            {t('multi_bonus_capped_label')}
          </small>
        )
      )}
      {next && (
        <small className={styles.slipNext}>
          {t('multi_bonus_next_label', {
            count: next.matches - n,
            percent: next.percent,
            odds,
          })}
        </small>
      )}
    </div>
  );
};

interface TicketProps {
  percent?: number;
  amount?: number;
  compact?: boolean;
  className?: string;
}

/**
 * The bonus on a ticket: the percentage it plays for and, once won, the
 * amount paid on top. Nothing for a ticket without a bonus.
 */
export const TicketMultiBonus = ({
  percent,
  amount,
  compact,
  className,
}: TicketProps) => {
  const { t } = useTranslation();
  if (!percent || percent <= 0) return null;
  return (
    <span
      className={classNames(
        styles.ticket,
        compact && styles.ticketCompact,
        className
      )}
      title={t('multi_bonus_label')}
    >
      <span className={styles.badge}>
        {t('multi_bonus_badge', { percent })}
      </span>
      {amount && amount > 0 ? (
        <span className={styles.ticketPaid}>
          {t('multi_bonus_paid_label')} <strong>+{money(amount)}</strong>
        </span>
      ) : (
        !compact && (
          <span className={styles.ticketText}>{t('multi_bonus_label')}</span>
        )
      )}
    </span>
  );
};
